import { Link } from "react-router-dom";
import useSWR from "swr";
import { ChevronLeft, ChevronRight, Layers } from "lucide-react";
import { fetchGameNav, listCollections } from "@/lib/api";
import { GAMES, blockStart } from "@/lib/games";

export const getGameLevel = (title) => {
  for (const g of Object.values(GAMES)) {
    const m = (title || "").match(g.levelRe);
    if (m) return { game: g, level: parseInt(m[1], 10) };
  }
  return null;
};

function NavButton({ item, dir }) {
  const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
  const label = dir === "prev" ? "Previous level" : "Next level";
  if (!item) {
    return (
      <span className="flex-1 rounded-xl border border-white/5 px-4 py-3 text-white/25 mono-label" data-testid={`level-nav-${dir}-disabled`}>
        {dir === "prev" ? "First level" : "Latest level"}
      </span>
    );
  }
  return (
    <Link
      to={`/video/${item.id}`}
      data-testid={`level-nav-${dir}`}
      className={`group flex-1 flex items-center gap-3 rounded-xl border border-white/10 bg-[#12141d] px-4 py-3 hover:border-[#d95d39]/60 transition-colors ${dir === "next" ? "flex-row-reverse text-right" : ""}`}
    >
      <Icon className="w-5 h-5 text-[#d95d39] flex-shrink-0 transition-transform group-hover:scale-110" />
      <div className="min-w-0">
        <div className="mono-label text-white/40">{label}</div>
        <div className="font-serif-editorial text-xl text-[#f4f1ea] group-hover:text-[#d95d39] transition-colors">
          Level {item.level.toLocaleString()}
        </div>
      </div>
    </Link>
  );
}

const RANGE_RE = /^Pet Rescue Saga level\s+(\d+)\s*-\s*(\d+)/i;

function useRange(game, level) {
  const isPrs = game.key === "pet-rescue";
  const { data } = useSWR(isPrs ? "/collections" : null, () => listCollections());
  if (!isPrs) {
    const start = blockStart(level);
    return { to: `${game.path}?block=${start}`, label: `Levels ${start}–${start + 99}` };
  }
  const c = (data?.items || []).find((x) => {
    const m = (x.title || "").match(RANGE_RE);
    return m && level >= parseInt(m[1], 10) && level <= parseInt(m[2], 10);
  });
  return c
    ? { to: `/collections/${c.slug}`, label: `${c.title.replace(/^Pet Rescue Saga\s+/i, "")} · ${c.count} videos` }
    : null;
}

function LevelBreadcrumb({ game, level }) {
  const range = useRange(game, level);
  return (
    <div className="flex flex-wrap items-center gap-2 mono-label text-white/40" data-testid="level-breadcrumb">
      <Layers className="w-3.5 h-3.5 text-[#d95d39]" />
      <Link to={game.path} data-testid="level-breadcrumb-saga" className="hover:text-[#d95d39] transition-colors">
        {game.name}
      </Link>
      {range && (
        <>
          <span>/</span>
          <Link
            to={range.to}
            data-testid="level-breadcrumb-range"
            className="text-[#f4f1ea]/80 hover:text-[#d95d39] transition-colors"
          >
            {range.label}
          </Link>
        </>
      )}
      <span>/</span>
      <span className="text-white/60">Level {level.toLocaleString()}</span>
    </div>
  );
}

export const LevelNav = ({ game, level }) => {
  const { data } = useSWR(`/levels/${game.key}/nav/${level}`, () => fetchGameNav(game.key, level));
  return (
    <div className="space-y-3">
      <LevelBreadcrumb game={game} level={level} />
      {data && (
        <nav aria-label={`${game.name} level navigation`} className="flex gap-3" data-testid="level-nav">
          <NavButton item={data.prev} dir="prev" />
          <NavButton item={data.next} dir="next" />
        </nav>
      )}
    </div>
  );
};
